import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';

import AdminPageLayout from '../components/admin/AdminPageLayout';
import ErrorBoundary from '../components/ErrorBoundary';
import ContractorList from '../components/admin/w9/ContractorList';
import CampaignResults from '../components/admin/w9/CampaignResults';
import w9Service from '../services/w9Service';
import logger from '../utils/logger';

/** Tax year defaults to the year just ended — that's the one being reported on. */
const DEFAULT_TAX_YEAR = new Date().getFullYear() - 1;
const DEFAULT_DUE_DATE = 'Friday, February 21st';

const W9CampaignContent = () => {
  const [taxYear, setTaxYear] = useState(DEFAULT_TAX_YEAR);
  const [dueDate, setDueDate] = useState(DEFAULT_DUE_DATE);

  const [data, setData] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  // Defaults come from the API so the copy lives in one place (the backend
  // template) rather than being duplicated here and drifting.
  const [defaults, setDefaults] = useState({ subject: '', message: '', placeholders: [] });

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const loadRecipients = useCallback(async (year) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await w9Service.getRecipients(year);
      setData(payload);
      setDefaults({
        subject: payload.defaultSubject || '',
        message: payload.defaultMessage || '',
        placeholders: payload.placeholders || []
      });
      // Only prefill an untouched composer — switching tax year must not discard
      // edits the admin has already made.
      setSubject((prev) => (prev.trim() ? prev : payload.defaultSubject || ''));
      setMessage((prev) => (prev.trim() ? prev : payload.defaultMessage || ''));
      // Auto-select everyone: the default action is to contact every contractor
      // who owes a W-9, and de-selecting is the deliberate exception.
      setSelectedEmails(payload.recipients.map((r) => r.email));
    } catch (err) {
      logger.log('[W9Campaign] Failed to load recipients:', err);
      setError(
        err.response?.data?.error || err.message || 'Could not load the contractor list.'
      );
      setData(null);
      setSelectedEmails([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecipients(taxYear);
  }, [taxYear, loadRecipients]);

  const toggle = (email) =>
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );

  const toggleAll = () =>
    setSelectedEmails((prev) =>
      prev.length === (data?.recipients.length || 0) ? [] : data.recipients.map((r) => r.email)
    );

  const trimmedDueDate = dueDate.trim();
  const trimmedSubject = subject.trim();
  const trimmedMessage = message.trim();
  const contentReady = trimmedSubject.length > 0 && trimmedMessage.length > 0;
  const isEdited =
    trimmedSubject !== defaults.subject.trim() || trimmedMessage !== defaults.message.trim();

  const canSend =
    !loading &&
    !sending &&
    selectedEmails.length > 0 &&
    trimmedDueDate.length > 0 &&
    contentReady;
  // Preview needs a due date and someone to render against, but not a full
  // selection — it never sends anything.
  const canPreview =
    !loading &&
    !sending &&
    !previewing &&
    selectedEmails.length > 0 &&
    trimmedDueDate.length > 0 &&
    contentReady;

  /**
   * Render against the first selected contractor, so the preview shows a name
   * and amount the admin is actually about to send — not a generic sample.
   */
  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const payload = await w9Service.previewEmail({
        taxYear,
        dueDate: trimmedDueDate,
        email: selectedEmails[0],
        subject: trimmedSubject,
        message: trimmedMessage
      });
      setPreview(payload);
    } catch (err) {
      logger.log('[W9Campaign] Preview failed:', err);
      setPreviewError(
        err.response?.data?.error || err.message || 'Could not render the email preview.'
      );
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setSendError(null);
    setResult(null);

    try {
      const payload = await w9Service.sendCampaign({
        taxYear,
        dueDate: trimmedDueDate,
        includeEmails: selectedEmails,
        subject: trimmedSubject,
        message: trimmedMessage
      });
      logger.log('[W9Campaign] Campaign complete:', payload.campaign);
      setResult(payload);
    } catch (err) {
      logger.log('[W9Campaign] Campaign failed:', err);
      setSendError(err.response?.data?.error || err.message || 'The campaign failed to send.');
    } finally {
      setSending(false);
    }
  };

  const yearOptions =
    data?.availableTaxYears?.length ? data.availableTaxYears : [DEFAULT_TAX_YEAR];

  return (
    <AdminPageLayout>
      <Typography variant="h4" component="h1" gutterBottom>
        W-9 Tax Campaign
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Request a Form W-9 from every contractor paid $600 or more in a tax year — the IRS
        threshold above which we must issue a 1099-NEC. Edit the wording below if you need
        to; the IRS download link, your postal address and the summary table are added
        automatically.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => loadRecipients(taxYear)}
            >
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not load contractors</AlertTitle>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              fullWidth
              label="Tax year"
              value={taxYear}
              onChange={(e) => setTaxYear(Number(e.target.value))}
              disabled={sending}
              helperText="Year the payments were made"
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={8} md={5}>
            <TextField
              fullWidth
              label="Return by"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={sending}
              error={trimmedDueDate.length === 0}
              helperText={
                trimmedDueDate.length === 0
                  ? 'A return-by date is required'
                  : 'Appears in the email exactly as written'
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1} sx={{ mt: { xs: 0, md: 1 } }}>
              <Button
                variant="contained"
                size="large"
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                disabled={!canSend}
                onClick={() => setConfirmOpen(true)}
                fullWidth
              >
                {sending ? 'Sending emails…' : 'Send W-9 Campaign'}
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  previewing ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />
                }
                disabled={!canPreview}
                onClick={handlePreview}
                fullWidth
              >
                {previewing ? 'Rendering…' : 'Preview email'}
              </Button>
              {!sending && selectedEmails.length === 0 && !loading && (
                <Typography variant="caption" color="text.secondary">
                  Select at least one contractor to send.
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap'
          }}
        >
          <Typography variant="h6">Email content</Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            disabled={sending || !isEdited}
            onClick={() => {
              setSubject(defaults.subject);
              setMessage(defaults.message);
            }}
          >
            Reset to default
          </Button>
        </Box>

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={sending}
            error={trimmedSubject.length === 0}
            helperText={trimmedSubject.length === 0 ? 'A subject is required' : ' '}
          />
          <TextField
            fullWidth
            multiline
            minRows={10}
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending}
            error={trimmedMessage.length === 0}
            helperText={
              trimmedMessage.length === 0
                ? 'A message is required'
                : 'Blank lines separate paragraphs. Lines starting with "1." or "-" become lists.'
            }
          />
          {defaults.placeholders.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Placeholders: {defaults.placeholders.join(' · ')} — replaced per recipient.
            </Typography>
          )}
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          Added automatically and not editable here: the payment/form/due summary, the IRS
          Form W-9 download button, and your postal address. This email carries no
          unsubscribe link, since a contractor cannot opt out of a tax form request.
        </Alert>
      </Paper>

      {sendError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button size="small" onClick={() => setConfirmOpen(true)} disabled={!canSend}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Campaign failed</AlertTitle>
          {sendError}
        </Alert>
      )}

      {result && (
        <Alert severity={result.campaign.failed > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
          <AlertTitle>
            {result.campaign.failed > 0 ? 'Campaign completed with failures' : 'Campaign sent'}
          </AlertTitle>
          {result.campaign.sent} of {result.campaign.totalRequested} W-9 request
          {result.campaign.totalRequested === 1 ? '' : 's'} delivered for {result.campaign.taxYear}.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        data && (
          <>
            {!data.hasData && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No reconciled payment data on file for {data.taxYear}.
              </Alert>
            )}
            <Typography variant="h6" gutterBottom>
              Qualifying contractors
            </Typography>
            <ContractorList
              recipients={data.recipients}
              skipped={data.skipped}
              selectedEmails={selectedEmails}
              onToggle={toggle}
              onToggleAll={toggleAll}
              taxYear={data.taxYear}
              threshold={data.threshold}
              disabled={sending}
            />
          </>
        )
      )}

      {result && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" gutterBottom>
            Campaign results
          </Typography>
          <CampaignResults result={result} />
        </>
      )}

      {previewError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setPreviewError(null)}>
          <AlertTitle>Could not render the preview</AlertTitle>
          {previewError}
        </Alert>
      )}

      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          Email preview
          {preview?.subject && (
            <Typography variant="body2" color="text.secondary">
              Subject: {preview.subject}
            </Typography>
          )}
          {preview?.contractor && (
            <Typography variant="body2" color="text.secondary">
              As {preview.contractor.name} would receive it
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Nothing has been sent. This reflects your current edits, with placeholders
            resolved for this contractor.
          </Alert>
          <Box
            component="iframe"
            title="w9-email-preview"
            srcDoc={preview?.html || ''}
            sx={{
              width: '100%',
              height: 480,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!canSend}
            onClick={() => {
              setPreview(null);
              setConfirmOpen(true);
            }}
          >
            Looks right — send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send {selectedEmails.length} W-9 request
          {selectedEmails.length === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This emails {selectedEmails.length} contractor
            {selectedEmails.length === 1 ? '' : 's'} about tax year {taxYear}, asking them to
            return a Form W-9 by <strong>{trimmedDueDate}</strong>. Emails send immediately and
            cannot be recalled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSend} startIcon={<SendIcon />}>
            Send now
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageLayout>
  );
};

const W9Campaign = () => (
  <ErrorBoundary>
    <W9CampaignContent />
  </ErrorBoundary>
);

export default W9Campaign;
